/**
 * Display helpers for playground scheduled-report cron expressions.
 *
 * Delegates cron → English to `cronstrue` so we cover the full POSIX
 * grammar (lists, ranges, steps) instead of just the daily / weekly /
 * monthly subset the dialog produces.
 *
 * Used by SchedulesList (list view) and ScheduleDialog (success view).
 */
import cronstrue from "cronstrue";

const describeCron = (cronExpr: string): string =>
  cronstrue.toString(cronExpr, {
    use24HourTimeFormat: true,
    throwExceptionOnParseError: true,
  });

/**
 * Render a stored cron expression as a human-readable cadence string,
 * e.g. "At 09:00, only on Friday UTC". Returns the raw cron when
 * `cronstrue` rejects it.
 */
export const formatCronFrequency = (cronExpr: string): string => {
  try {
    const description = describeCron(cronExpr);
    return `${description} UTC`;
  } catch {
    return cronExpr;
  }
};

/**
 * Render the create-success message body — sentence fragment after
 * "Reports will be sent ", e.g. "at 09:00, only on Friday UTC". Builds a
 * synthetic cron from the form values and routes through
 * `formatCronFrequency` so list and dialog stay phrased identically. The
 * leading "At" is lowercased so the fragment fits mid-sentence.
 */
export const formatSuccessFrequency = (
  frequency: string,
  hour: string,
  dayOfWeek: string,
  dayOfMonth: string
): string => {
  let cron: string;
  if (frequency === "weekly") {
    cron = `0 ${hour} * * ${dayOfWeek}`;
  } else if (frequency === "monthly") {
    cron = `0 ${hour} ${dayOfMonth} * *`;
  } else {
    cron = `0 ${hour} * * *`;
  }
  return formatCronFrequency(cron).replace(/^At /, "at ");
};
